import type { EnemyDef } from '../../content/enemies/enemies';
import type { Element } from '../game/elements';
import type { CombatState, PlayerState } from '../game/types';
import { resolveEnemyTurn } from './resolveEnemyTurn';
import {
  actionBlockLabel,
  applyEnemyDamageOverTime,
  rollActionBlock,
  tickStatuses,
  type ActionBlockReason,
} from './statuses';
import { applyPlayerSkill, type SkillLike } from './useSkill';

export interface CombatDamageEvent {
  source: 'skill' | 'status';
  damage: number;
  element?: Element;
}

export type CombatTurnResult =
  | { ok: false }
  | {
      ok: true;
      player: PlayerState;
      combat: CombatState;
      enemyDefeated: boolean;
      playerKilled: boolean;
      attackMissed: boolean;
      damageEvents: CombatDamageEvent[];
    };

function tickRound(combat: CombatState): CombatState {
  return {
    ...combat,
    playerStatuses: tickStatuses(combat.playerStatuses),
    enemyStatuses: tickStatuses(combat.enemyStatuses),
  };
}

/** Disabled player spends no mana, loses the action, then enemy acts. */
function resolveBlockedPlayerTurn(
  player: PlayerState,
  combat: CombatState,
  enemy: EnemyDef,
  reason: ActionBlockReason,
  rng: () => number,
): CombatTurnResult {
  const blockedCombat = {
    ...combat,
    log: [`Bạn ${actionBlockLabel(reason)} — mất lượt!`, ...combat.log].slice(0, 8),
  };
  const enemyResult = resolveEnemyTurn(player, blockedCombat, enemy, rng);
  return {
    ok: true,
    player: enemyResult.player,
    combat: enemyResult.killed
      ? { ...enemyResult.combat, playerDefeated: true }
      : tickRound(enemyResult.combat),
    enemyDefeated: false,
    playerKilled: enemyResult.killed,
    attackMissed: false,
    damageEvents: [],
  };
}

/** Player action → DOT → enemy action → status tick. */
export function resolvePlayerCombatTurn(
  player: PlayerState,
  combat: CombatState,
  skill: SkillLike,
  enemy: EnemyDef,
  rng: () => number = Math.random,
): CombatTurnResult {
  const playerBlocked = rollActionBlock(combat.playerStatuses, rng);
  if (playerBlocked) {
    return resolveBlockedPlayerTurn(player, combat, enemy, playerBlocked, rng);
  }

  const skillResult = applyPlayerSkill(player, combat, skill, enemy, rng);
  if (!skillResult.ok) return { ok: false };

  const skillDamage = Math.max(0, combat.enemyHp - skillResult.combat.enemyHp);
  const skillDamageEvents: CombatDamageEvent[] = skillDamage > 0
    ? [{ source: 'skill', damage: skillDamage, element: skill.element as Element }]
    : [];

  if (skillResult.defeated) {
    return {
      ok: true,
      player: skillResult.player,
      combat: skillResult.combat,
      enemyDefeated: true,
      playerKilled: false,
      attackMissed: skillResult.missed,
      damageEvents: skillDamageEvents,
    };
  }

  const damageOverTime = applyEnemyDamageOverTime(skillResult.combat);
  const damageEvents: CombatDamageEvent[] = damageOverTime.damage > 0
    ? [...skillDamageEvents, { source: 'status', damage: damageOverTime.damage }]
    : skillDamageEvents;

  if (damageOverTime.defeated) {
    return {
      ok: true,
      player: skillResult.player,
      combat: damageOverTime.combat,
      enemyDefeated: true,
      playerKilled: false,
      attackMissed: skillResult.missed,
      damageEvents,
    };
  }

  const enemyResult = resolveEnemyTurn(
    skillResult.player,
    damageOverTime.combat,
    enemy,
    rng,
  );
  return {
    ok: true,
    player: enemyResult.player,
    combat: enemyResult.killed
      ? { ...enemyResult.combat, playerDefeated: true }
      : tickRound(enemyResult.combat),
    enemyDefeated: false,
    playerKilled: enemyResult.killed,
    attackMissed: skillResult.missed,
    damageEvents,
  };
}
